"use client";

import { X } from "lucide-react";
import { NotificationItem as NotificationItemType } from "@/types/notification.types";
import NotificationTypeIcon from "@/app/components/notifications/NotificationTypeIcon";
import {
  canDiscardNotification,
  getNotificationItemPriorityTheme,
} from "@/app/lib/util/notification-priority.util";
import { formatNotificationRelativeTime } from "@/app/lib/util/notification-time.util";

interface NotificationItemProps {
  item: NotificationItemType;
  onClick: () => void;
  onDiscard?: () => void;
}

export default function NotificationItem({
  item,
  onClick,
  onDiscard,
}: NotificationItemProps) {
  const theme = getNotificationItemPriorityTheme(item);
  const timestamp = item.created_at?._seconds
    ? formatNotificationRelativeTime(item.created_at._seconds)
    : "";
  const showDiscard = onDiscard && canDiscardNotification(item);

  return (
    <div
      className={[
        "group relative flex items-start gap-1 rounded-xl shadow-sm transition-colors duration-150",
        item.is_read ? theme.readItem : theme.unreadItem,
        item.is_read ? "" : theme.unreadAccent,
        theme.hoverItem,
      ].join(" ")}
    >
      <button
        id={`notification-item-${item.id}`}
        type="button"
        onClick={onClick}
        className="flex min-w-0 flex-1 items-start gap-3 px-4 py-3.5 text-left"
      >
        <NotificationTypeIcon
          type={item.type}
          priority={
            item.priority ?? (item.data?.priority as string | undefined)
          }
        />
        <div className="min-w-0 flex-1 pt-0.5">
          <div className="flex items-start justify-between gap-2">
            <p
              className={`text-sm font-semibold leading-snug ${
                item.is_read ? theme.readTitle : theme.unreadTitle
              }`}
            >
              {item.title}
            </p>
            <div className="flex shrink-0 items-center gap-1.5">
              <span
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold leading-none ${theme.legendChip}`}
              >
                <span
                  className={`h-1.5 w-1.5 rounded-full ${theme.unreadDot}`}
                  aria-hidden="true"
                />
                {theme.label}
              </span>
            </div>
          </div>
          <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-color-gray">
            {item.message}
          </p>
          {timestamp && (
            <p className="mt-2 text-[11px] text-color-gray-border transition-colors group-hover:text-color-gray">
              {timestamp}
            </p>
          )}
        </div>
      </button>

      {showDiscard && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onDiscard();
          }}
          className="mr-2 mt-3 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-color-gray opacity-100 transition-all hover:bg-color-gray-light hover:text-color-font-dark md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100 focus:opacity-100"
          aria-label="Dismiss notification"
          title="Dismiss without opening"
        >
          <X size={16} strokeWidth={1.75} />
        </button>
      )}
    </div>
  );
}
